import { useScannerOverride } from "../../contexts/ScannerContext";

interface ScanTimerToggleProps {
  /** Required to finish (stop) a running session. */
  userId?: string;
  hasActiveSession: boolean;
  isSessionsLoading: boolean;
  isSessionActionLoading: boolean;
  onStart: () => void;
  onFinish: (userId: string) => void;
}

/**
 * Registers a scanner override that toggles a single start/finish timer, the
 * same way scanning a job's vehicle drives the assembly timer in JobWorkspace:
 * scanning a vehicle starts the timer when idle and finishes it when one is
 * running. This page is already dedicated to a single record, so any vehicle
 * scan drives its timer (we intentionally don't match on vehicle id — printed
 * tags can carry a different vehicle record than the one the record was created
 * against, e.g. duplicate VRMs). Render only where this should own the scanner
 * (a standalone detail page), never while embedded in a page with its own scan
 * handling. Renders nothing.
 */
export default function ScanTimerToggle({
  userId,
  hasActiveSession,
  isSessionsLoading,
  isSessionActionLoading,
  onStart,
  onFinish,
}: ScanTimerToggleProps) {
  useScannerOverride((scan) => {
    const { parts } = scan;
    // Only vehicle scans ("v|{vehicleId}|{vrm}") drive the timer.
    if (parts[0]?.toLowerCase() !== "v" || !parts[1]) return;
    // Ignore scans while sessions are still resolving or a start/finish is
    // already in flight, to avoid double-toggling.
    if (isSessionsLoading || isSessionActionLoading) return;

    if (hasActiveSession) {
      if (userId) onFinish(userId);
    } else {
      onStart();
    }
  });

  return null;
}
